import { useEffect, useState } from 'react';
import { devToolsUnlocked } from './dev-tools';

/**
 * Dev-only simulator for the low/out-of-credits widgets — force a state so the drawer card, the banner
 * and the chat widget can be seen without an account that is actually low or out. It OVERRIDES both the
 * feature flag and the real balance (see `useCreditsWidgetState`), and it is applied LIVE (a window
 * event, not a reload), so flipping it mid-conversation shows the chat widget appear in place — which is
 * exactly the case that is otherwise hard to reproduce.
 *
 * Gated on `devToolsUnlocked()` like every other dev override: a real user's browser holds no grant, so
 * a key left in storage does nothing.
 */
export type CreditsWidgetSim =
  | 'low-daily'
  | 'out-daily'
  // Monthly, by tier. The plain `-monthly` pair is the PAID plan (plan name + renewal date); `-free`
  // is a free plan metered monthly (no plan/date, "Upgrade"); `-maxplus` is the top tier (date-free
  // "Add credits…" body, "Add Credits"). There is no daily case but free (daily = the free plan).
  | 'low-monthly-free'
  | 'out-monthly-free'
  | 'low-monthly'
  | 'out-monthly'
  | 'low-monthly-maxplus'
  | 'out-monthly-maxplus';

const SIMS: CreditsWidgetSim[] = [
  'low-daily',
  'out-daily',
  'low-monthly-free',
  'out-monthly-free',
  'low-monthly',
  'out-monthly',
  'low-monthly-maxplus',
  'out-monthly-maxplus',
];
const KEY = 'orion-web:dev-credits-widget-sim';
const EVENT = 'orion:credits-widget-sim';

function isSim(value: unknown): value is CreditsWidgetSim {
  return typeof value === 'string' && (SIMS as string[]).includes(value);
}

/** Whether a sim forces the top-tier ("Max+") CTA wording ("Buy more credits"). Dev-only. */
export function simIsMaxPlus(sim: CreditsWidgetSim | null): boolean {
  return sim === 'low-monthly-maxplus' || sim === 'out-monthly-maxplus';
}

/** The forced state, or null for "use the real state". Null without a dev-tools grant, always. */
export function getCreditsWidgetSim(): CreditsWidgetSim | null {
  if (!devToolsUnlocked()) return null;
  try {
    const value = window.localStorage.getItem(KEY);
    return isSim(value) ? value : null;
  } catch {
    return null;
  }
}

/** Set or clear the forced state and notify mounted widgets in this tab (no reload). */
export function setCreditsWidgetSim(value: CreditsWidgetSim | null): void {
  if (!devToolsUnlocked()) return;
  try {
    if (value) window.localStorage.setItem(KEY, value);
    else window.localStorage.removeItem(KEY);
  } catch {
    /* storage blocked — the override simply does not persist */
  }
  try {
    window.dispatchEvent(new Event(EVENT));
  } catch {
    /* SSR / no window */
  }
}

/** Live subscription to the forced state — repaints every widget the instant the dev panel changes it,
 *  and picks up a change made in another tab via the `storage` event. */
export function useCreditsWidgetSim(): CreditsWidgetSim | null {
  const [sim, setSim] = useState<CreditsWidgetSim | null>(null);
  useEffect(() => {
    const update = () => setSim(getCreditsWidgetSim());
    update(); // seed after mount (SSR-safe: getCreditsWidgetSim reads window)
    window.addEventListener(EVENT, update);
    window.addEventListener('storage', update);
    return () => {
      window.removeEventListener(EVENT, update);
      window.removeEventListener('storage', update);
    };
  }, []);
  return sim;
}

// --- "appear after N messages" mode -----------------------------------------------------------------
// A dev sim mode that holds the forced state back until the user has SENT this many messages, to
// reproduce credits running out mid-conversation. The count is session-global (any chat) and in-memory.
export const SIM_DELAY_MESSAGE_THRESHOLD = 3;
const DELAY_KEY = 'orion-web:dev-credits-widget-sim-delay';
const DELAY_EVENT = 'orion:credits-widget-sim-delay';
const COUNT_EVENT = 'orion:credits-widget-sim-count';
let simMessageCount = 0;

/** Whether the "appear after N messages" mode is armed. False without a dev-tools grant. */
export function getCreditsWidgetSimDelay(): boolean {
  if (!devToolsUnlocked()) return false;
  try {
    return window.localStorage.getItem(DELAY_KEY) === '1';
  } catch {
    return false;
  }
}

/** Arm/disarm the delay mode; resets the message counter so each arming starts a fresh countdown. */
export function setCreditsWidgetSimDelay(on: boolean): void {
  if (!devToolsUnlocked()) return;
  try {
    if (on) window.localStorage.setItem(DELAY_KEY, '1');
    else window.localStorage.removeItem(DELAY_KEY);
  } catch {
    /* storage blocked */
  }
  simMessageCount = 0;
  try {
    window.dispatchEvent(new Event(DELAY_EVENT));
    window.dispatchEvent(new Event(COUNT_EVENT));
  } catch {
    /* SSR / no window */
  }
}

export function useCreditsWidgetSimDelay(): boolean {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const update = () => setOn(getCreditsWidgetSimDelay());
    update();
    window.addEventListener(DELAY_EVENT, update);
    window.addEventListener('storage', update);
    return () => {
      window.removeEventListener(DELAY_EVENT, update);
      window.removeEventListener('storage', update);
    };
  }, []);
  return on;
}

/** Count a sent message — called from the chat send path. No-op unless the delay mode is armed
 *  (which itself requires a dev-tools grant), so it is inert in production. */
export function noteCreditsWidgetSimMessage(): void {
  if (!getCreditsWidgetSimDelay()) return;
  simMessageCount += 1;
  try {
    window.dispatchEvent(new Event(COUNT_EVENT));
  } catch {
    /* SSR / no window */
  }
}

export function useCreditsWidgetSimMessageCount(): number {
  const [n, setN] = useState(0);
  useEffect(() => {
    const update = () => setN(simMessageCount);
    update();
    window.addEventListener(COUNT_EVENT, update);
    return () => window.removeEventListener(COUNT_EVENT, update);
  }, []);
  return n;
}
